import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FacultyLayout } from '../../components/FacultyLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { FileText, Upload, Plus, X, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacultySyllabus = () => {
    const { token } = useAuth();
    const [courses, setCourses] = useState([]);
    const [syllabusMap, setSyllabusMap] = useState({}); // { course_id: [syllabus entries] }
    const [facultyRecord, setFacultyRecord] = useState(null);
    const [loading, setLoading] = useState(true);

    // Editor state
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [syllabusEditor, setSyllabusEditor] = useState({ file_url: '', chapters: [] });
    const [syllabusFile, setSyllabusFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [coursesRes, facultyRes] = await Promise.all([
                axios.get(`${API_URL}/api/faculty/my-courses`, { headers }),
                axios.get(`${API_URL}/api/faculty/me`, { headers }),
            ]);
            setCourses(coursesRes.data);
            setFacultyRecord(facultyRes.data);

            // Fetch syllabus for each course
            const syllabusData = {};
            await Promise.all(coursesRes.data.map(async (course) => {
                const res = await axios.get(`${API_URL}/api/syllabus?course_id=${course.id}`, { headers });
                syllabusData[course.id] = res.data;
            }));
            setSyllabusMap(syllabusData);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const getSubjectsForCourse = (course) => {
        if (!facultyRecord?.subject_specialization?.length) return course.subjects || [];
        const mySubjects = facultyRecord.subject_specialization.map(s => s.toLowerCase());
        const filtered = (course.subjects || []).filter(s => mySubjects.includes(s.toLowerCase()));
        return filtered.length > 0 ? filtered : course.subjects || [];
    };

    const selectSubject = (course, subject) => {
        setSelectedCourse(course);
        setSelectedSubject(subject);
        const existing = (syllabusMap[course.id] || []).find(s => s.subject === subject);
        setSyllabusEditor({ file_url: existing?.file_url || '', chapters: existing?.chapters || [] });
        setSyllabusFile(null);
    };

    const addChapter = () => setSyllabusEditor(p => ({ ...p, chapters: [...p.chapters, { name: '', topics: [] }] }));
    const updateChapterName = (ci, name) => setSyllabusEditor(p => ({ ...p, chapters: p.chapters.map((c, i) => i === ci ? { ...c, name } : c) }));
    const removeChapter = (ci) => setSyllabusEditor(p => ({ ...p, chapters: p.chapters.filter((_, i) => i !== ci) }));
    const addTopic = (ci) => setSyllabusEditor(p => ({ ...p, chapters: p.chapters.map((c, i) => i === ci ? { ...c, topics: [...c.topics, ''] } : c) }));
    const updateTopic = (ci, ti, value) => setSyllabusEditor(p => ({ ...p, chapters: p.chapters.map((c, i) => i === ci ? { ...c, topics: c.topics.map((t, j) => j === ti ? value : t) } : c) }));
    const removeTopic = (ci, ti) => setSyllabusEditor(p => ({ ...p, chapters: p.chapters.map((c, i) => i === ci ? { ...c, topics: c.topics.filter((_, j) => j !== ti) } : c) }));

    const saveSyllabus = async () => {
        if (!selectedCourse || !selectedSubject) return;
        const headers = { Authorization: `Bearer ${token}` };
        let file_url = syllabusEditor.file_url || null;
        try {
            setSaving(true);
            if (syllabusFile) {
                const uploadForm = new FormData();
                uploadForm.append('file', syllabusFile);
                const uploadRes = await axios.post(`${API_URL}/api/uploads?file_type=study_material`, uploadForm, { headers });
                file_url = uploadRes.data.file_url;
            }
            await axios.post(`${API_URL}/api/syllabus`, {
                course_id: selectedCourse.id,
                subject: selectedSubject,
                file_url,
                chapters: syllabusEditor.chapters.filter(c => c.name.trim())
            }, { headers });
            toast.success('Syllabus saved!');
            setSyllabusFile(null);
            // Refresh syllabus for this course
            const res = await axios.get(`${API_URL}/api/syllabus?course_id=${selectedCourse.id}`, { headers });
            setSyllabusMap(prev => ({ ...prev, [selectedCourse.id]: res.data }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save syllabus');
        } finally {
            setSaving(false);
        }
    };

    return (
        <FacultyLayout title="Syllabus Management">
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            ) : courses.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center">
                        <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                        <h3 className="font-heading text-xl font-semibold mb-2">No Courses Assigned</h3>
                        <p className="text-muted-foreground">You are not assigned to any courses yet.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left: Course + Subject List */}
                    <div className="space-y-4">
                        {courses.map(course => {
                            const subjects = getSubjectsForCourse(course);
                            const courseSyllabus = syllabusMap[course.id] || [];
                            return (
                                <Card key={course.id}>
                                    <CardContent className="p-4">
                                        <h3 className="font-semibold text-sm mb-3 truncate">{course.name}</h3>
                                        <div className="space-y-1">
                                            {subjects.length === 0 ? (
                                                <p className="text-xs text-muted-foreground">No subjects in this course.</p>
                                            ) : (
                                                subjects.map(subject => {
                                                    const hasSyllabus = courseSyllabus.some(s => s.subject === subject && (s.chapters?.length > 0 || s.file_url));
                                                    const isSelected = selectedCourse?.id === course.id && selectedSubject === subject;
                                                    return (
                                                        <button
                                                            key={subject}
                                                            onClick={() => selectSubject(course, subject)}
                                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                                                                isSelected ? 'bg-primary text-white' : 'hover:bg-muted text-foreground'
                                                            }`}
                                                        >
                                                            <span className="truncate">{subject}</span>
                                                            {hasSyllabus && (
                                                                <Badge variant={isSelected ? 'outline' : 'secondary'} className={`text-xs shrink-0 ${isSelected ? 'border-white/50 text-white' : ''}`}>
                                                                    Added
                                                                </Badge>
                                                            )}
                                                        </button>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Right: Editor */}
                    <div className="lg:col-span-2">
                        {!selectedSubject ? (
                            <Card>
                                <CardContent className="py-16 text-center">
                                    <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                                    <p className="text-muted-foreground">Select a subject from the left to edit its syllabus</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card>
                                <CardContent className="p-6 space-y-5">
                                    <div>
                                        <h3 className="font-heading text-lg font-semibold">{selectedSubject}</h3>
                                        <p className="text-sm text-muted-foreground">{selectedCourse?.name}</p>
                                    </div>

                                    {/* File Upload */}
                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-muted-foreground uppercase">Subject PDF (optional)</Label>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <label htmlFor="faculty-syllabus-file" className="flex items-center gap-1.5 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-muted">
                                                <Upload className="w-4 h-4" />
                                                {syllabusFile ? syllabusFile.name : 'Upload PDF'}
                                            </label>
                                            <input id="faculty-syllabus-file" type="file" accept=".pdf,.doc,.docx" className="hidden"
                                                onChange={(e) => { const f = e.target.files[0]; if (f) { setSyllabusFile(f); setSyllabusEditor(p => ({ ...p, file_url: '' })); } e.target.value = ''; }} />
                                            {syllabusFile ? (
                                                <Button type="button" variant="ghost" size="icon" onClick={() => setSyllabusFile(null)}>
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            ) : (
                                                <Input placeholder="or paste file URL" value={syllabusEditor.file_url}
                                                    onChange={(e) => setSyllabusEditor(p => ({ ...p, file_url: e.target.value }))}
                                                    className="flex-1 h-9 text-sm" />
                                            )}
                                            {syllabusEditor.file_url && !syllabusFile && (
                                                <a href={syllabusEditor.file_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">View</a>
                                            )}
                                        </div>
                                    </div>

                                    <Separator />

                                    {/* Chapters */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs text-muted-foreground uppercase">Chapters</Label>
                                            <Button type="button" variant="outline" size="sm" onClick={addChapter}>
                                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Chapter
                                            </Button>
                                        </div>

                                        {syllabusEditor.chapters.length === 0 ? (
                                            <p className="text-sm text-muted-foreground text-center py-6">
                                                No chapters yet. Add chapters or just save with a file.
                                            </p>
                                        ) : (
                                            syllabusEditor.chapters.map((chapter, ci) => (
                                                <div key={ci} className="border rounded-lg p-4 space-y-3">
                                                    <div className="flex items-center gap-2">
                                                        <Input
                                                            placeholder={`Chapter ${ci + 1} name`}
                                                            value={chapter.name}
                                                            onChange={(e) => updateChapterName(ci, e.target.value)}
                                                            className="font-medium flex-1"
                                                        />
                                                        <Button type="button" variant="ghost" size="icon" className="text-destructive" onClick={() => removeChapter(ci)}>
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                    <div className="pl-3 space-y-2">
                                                        {chapter.topics.map((topic, ti) => (
                                                            <div key={ti} className="flex items-center gap-2">
                                                                <span className="text-muted-foreground text-sm">•</span>
                                                                <Input
                                                                    placeholder={`Topic ${ti + 1}`}
                                                                    value={topic}
                                                                    onChange={(e) => updateTopic(ci, ti, e.target.value)}
                                                                    className="h-8 text-sm flex-1"
                                                                />
                                                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeTopic(ci, ti)}>
                                                                    <X className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        ))}
                                                        <Button type="button" variant="ghost" size="sm" className="text-muted-foreground text-xs" onClick={() => addTopic(ci)}>
                                                            <Plus className="w-3 h-3 mr-1" /> Add Topic
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <Button onClick={saveSyllabus} disabled={saving} className="w-full">
                                        {saving ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                                        ) : (
                                            'Save Syllabus'
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            )}
        </FacultyLayout>
    );
};

export default FacultySyllabus;
